import { computed, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import couponService, { type Coupon } from "../services/coupon";

type CouponSaveError = {
  response?: {
    status?: number;
    data?: {
      errors?: Record<string, string[]>;
      message?: string;
    };
  };
};

export function useCoupon() {
  const { t } = useI18n();
  const coupons = ref<Coupon[]>([]);
  const searchQuery = ref("");
  const showModal = ref(false);
  const selectedCoupon = ref<Coupon | null>(null);
  const loading = ref(false);
  const errors = ref<Record<string, string[]>>({});

  const form = ref<Partial<Coupon>>({
    code: "",
    name: "",
    type: "fixed",
    discount: 0,
    start_date: "",
    end_date: "",
    limit: 0,
    status: 1,
    description: "",
  });

  const fetchCoupons = async () => {
    loading.value = true;
    try {
      const response = await couponService.getCoupons();
      coupons.value = response.data;
    } catch (error) {
      console.error("Failed to fetch coupons:", error);
      Swal.fire({
        icon: "error",
        title: t("coupon_page.load_failed"),
        text: t("coupon_page.load_failed_text"),
      });
    } finally {
      loading.value = false;
    }
  };

  const filteredCoupons = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return coupons.value;

    return coupons.value.filter((coupon) =>
      [coupon.code, coupon.name, coupon.type, coupon.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  });

  const stats = computed(() => {
    const total = coupons.value.length;
    const active = coupons.value.filter(
      (coupon) => String(coupon.status ?? 1) !== "0" && String(coupon.status ?? 1) !== "false",
    ).length;

    return {
      total,
      active,
      inactive: Math.max(total - active, 0),
    };
  });

  const pageSizeOptions = ref(["10", "25", "50", "100", "1000"]);

  const paginationConfig = computed(() => ({
    showSizeChanger: true,
    pageSizeOptions: pageSizeOptions.value,
    showTotal: (total: number, range: [number, number]) =>
      t("coupon_page.pagination_total", {
        start: range[0],
        end: range[1],
        total,
      }),
    defaultPageSize: 10,
    locale: { items_per_page: t("coupon_page.items_per_page") },
  }));

  watch(
    filteredCoupons,
    (newValue) => {
      const totalCount = newValue.length > 0 ? newValue.length : 1000;
      pageSizeOptions.value = ["10", "25", "50", "100", String(totalCount)];
    },
    { immediate: true },
  );

  const resetForm = () => {
    form.value = {
      code: "",
      name: "",
      type: "fixed",
      discount: 0,
      start_date: "",
      end_date: "",
      limit: 0,
      status: 1,
      description: "",
    };
    errors.value = {};
  };

  const openCreateModal = () => {
    selectedCoupon.value = null;
    resetForm();
    showModal.value = true;
  };

  const openEditModal = (coupon: Coupon) => {
    selectedCoupon.value = coupon;
    errors.value = {};
    form.value = {
      id: coupon.id,
      code: coupon.code || "",
      name: coupon.name || "",
      type: coupon.type || "fixed",
      discount: Number(coupon.discount ?? 0),
      start_date: coupon.start_date || "",
      end_date: coupon.end_date || "",
      limit: Number(coupon.limit ?? 0),
      status: String(coupon.status) === "0" || String(coupon.status) === "false" ? 0 : 1,
      description: coupon.description || "",
    };
    showModal.value = true;
  };

  const closeModal = () => {
    showModal.value = false;
    errors.value = {};
  };

  const updateField = (field: keyof Coupon, value: string | number | undefined) => {
    form.value = {
      ...form.value,
      [field]: value,
    };
  };

  const generateCouponCode = async () => {
    try {
      if (!coupons.value.length) {
        await fetchCoupons();
      }

      const nextNumber =
        coupons.value
          .map((item) => {
            const match = String(item.code || "").match(/(\d+)\s*$/);
            return match ? Number(match[1]) : 0;
          })
          .reduce((max, current) => Math.max(max, current), 0) + 1;

      form.value.code = `CPN${String(nextNumber).padStart(3, "0")}`;
    } catch (error) {
      console.error("Failed to generate coupon code:", error);
      Swal.fire({
        icon: "error",
        title: t("coupon_page.generate_failed"),
        text: t("coupon_page.generate_failed_text"),
      });
    }
  };

  const saveCoupon = async () => {
    loading.value = true;
    errors.value = {};
    try {
      const payload: Coupon = {
        code: form.value.code || "",
        name: form.value.name || "",
        type: form.value.type || "fixed",
        discount: Number(form.value.discount ?? 0),
        start_date: form.value.start_date || "",
        end_date: form.value.end_date || "",
        limit: Number(form.value.limit ?? 0),
        status: Number(form.value.status ?? 1),
        description: form.value.description || undefined,
      };

      if (selectedCoupon.value?.id) {
        await couponService.updateCoupon(selectedCoupon.value.id, payload);
      } else {
        await couponService.createCoupon(payload);
      }

      await fetchCoupons();
      await Swal.fire({
        icon: "success",
        title: selectedCoupon.value ? t("coupon_page.updated_title") : t("coupon_page.created_title"),
        timer: 1400,
        showConfirmButton: false,
      });
      closeModal();
    } catch (error: unknown) {
      const requestError = error as CouponSaveError;
      console.error(error);
      if (requestError.response?.status === 422) {
        errors.value = requestError.response.data?.errors || {};
      } else {
        const message =
          requestError.response?.data?.message ||
          Object.values(requestError.response?.data?.errors || {}).flat()[0] ||
          t("coupon_page.save_failed_text");
        Swal.fire({
          icon: "error",
          title: t("coupon_page.save_failed"),
          text: message as string,
        });
      }
    } finally {
      loading.value = false;
    }
  };

  const deleteCoupon = async (coupon: Coupon) => {
    if (!coupon?.id) return;

    const result = await Swal.fire({
      title: t("coupon_page.delete_title"),
      text: t("coupon_page.delete_text", { name: coupon.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("coupon_page.delete_confirm"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("coupon_page.deleting_title"),
        text: t("coupon_page.deleting_text"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await couponService.deleteCoupon(coupon.id);
      await fetchCoupons();

      Swal.fire({
        icon: "success",
        title: t("coupon_page.deleted_title"),
        text: t("coupon_page.deleted_text", { name: coupon.name }),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: t("coupon_page.delete_failed"),
        text: t("coupon_page.delete_failed_text"),
      });
    }
  };

  return {
    searchQuery,
    filteredCoupons,
    stats,
    showModal,
    selectedCoupon,
    form,
    errors,
    loading,
    paginationConfig,
    fetchCoupons,
    openCreateModal,
    openEditModal,
    closeModal,
    updateField,
    generateCouponCode,
    saveCoupon,
    deleteCoupon,
  };
}
